import { Trans, useLingui } from '@lingui/react/macro'
import { Center, Loader } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { AppPage } from '@markku/components'
import { IconCircleCheck, IconX } from '@tabler/icons-react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'
import { visitHooks } from '../../../lib/trpcHooks'
import { propertyHooks } from '../../../lib/trpcHooks/propertyHooks'
import { PastVisit } from '../../../modules/visit/PastVisit'
import { FutureVisit } from '../../../modules/visit/FutureVisit'

const searchSchema = z.object({
  payment: z.enum(['success', 'cancelled']).optional(),
})

function VisitDetailPage() {
  const { t } = useLingui()
  const { id } = Route.useParams()
  const visitId = Number(id)

  const { data: visit, isLoading: isLoadingVisit } =
    visitHooks.useGetById(visitId)
  const { data: property, isLoading: isLoadingProperty } =
    propertyHooks.useGetById(visit?.propertyId ?? 0, {
      enabled: !!visit?.propertyId,
    })

  const isLoading = isLoadingVisit || isLoadingProperty

  if (isLoading) {
    return (
      <AppPage title={t`Visit`} backTarget="/visits">
        <Center py="xl">
          <Loader size="md" />
        </Center>
      </AppPage>
    )
  }

  if (!visit) {
    return (
      <AppPage title={t`Visit not found`} backTarget="/visits">
        <Center py="xl">
          <Trans>Visit not found</Trans>
        </Center>
      </AppPage>
    )
  }

  // Check if visit is in the past
  const isPastVisit = new Date(visit.startTime) < new Date()

  return (
    <AppPage
      title={isPastVisit ? t`Past visit` : t`Upcoming visit`}
      backTarget="/visits"
    >
      {isPastVisit ? (
        <PastVisit visit={visit} property={property} />
      ) : (
        <FutureVisit visit={visit} />
      )}
    </AppPage>
  )
}

export const Route = createFileRoute('/_app/visits/$id')({
  validateSearch: searchSchema,
  beforeLoad: ({ search, params }) => {
    // Note: These notification messages use hardcoded English because they run before
    // the i18n context is available in beforeLoad. The LinguiJS setup requires hooks
    // which can only be used inside React components.
    if (search.payment === 'success') {
      notifications.show({
        title: 'Payment successful!',
        message:
          'Thank you for your payment. You will receive a confirmation email.',
        color: 'green',
        icon: <IconCircleCheck />,
      })
      throw redirect({
        to: '/visits/$id',
        params: { id: params.id },
      })
    } else if (search.payment === 'cancelled') {
      notifications.show({
        title: 'Payment cancelled',
        message: 'You can try again anytime',
        color: 'yellow',
        icon: <IconX />,
      })
      throw redirect({
        to: '/visits/$id',
        params: { id: params.id },
      })
    }
  },
  component: VisitDetailPage,
})
